/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useState, useRef, useCallback } from 'react';
import cx from 'classnames';
import { SmartLink as Link } from '@/utils/smartLink';
import azCommonStyles from '@/theme/globals.module.scss';
import { useLabels } from '@/hooks/useLabels';
import styles from './ShopByModelList.module.scss';
import DeviceWrapper from '@/components/AZCustomComponent/DeviceWrapper';
import { Grid } from '@/components/Grid';
import Title from '@/components/AZCustomComponent/Title';
import { useSelector } from 'react-redux';
import { type ReduxState } from '../../../types';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const labelMap = {
  label_Show_All: 'label_Show_All',
  label_Show_Less: 'label_Show_Less',
};

export type ShopByModelListProps = {
  data: {
    title: string;
    desktop: boolean;
    tablet: boolean;
    mobile: boolean;
    canonicalPath: string | undefined;
    taxonomyPath: string | undefined;
    pageType: string | undefined;
    shopByMMYList: Array<{
      name: string | undefined;
      seoUrl: string | undefined;
      taxonomyPath?: string;
      makeModelYearPath?: string;
    }>;
  };
};

export const ShopByModelList = ({ data }: ShopByModelListProps) => {
  const { title, shopByMMYList } = data;
  const shopByMMYListLength = shopByMMYList.length;
  const labels = useLabels(labelMap);
  const ref = useRef<HTMLDivElement>(null);
  const deviceType = useSelector<ReduxState>(({ appData }) => appData.deviceType);
  const [readMore, setReadMore] = useState(deviceType === 'bot');

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.only('md'));

  const toggleReadMore = useCallback(() => setReadMore((prev) => !prev), []);

  if (shopByMMYListLength === 0) {
    return null;
  }

  const maxOptions = isMobile ? 5 : isTablet ? 10 : 15;

  const filteredList = readMore ? shopByMMYList : shopByMMYList.slice(0, maxOptions);

  return (
    <div>
      <DeviceWrapper
        desktop={Boolean(data.desktop)}
        tablet={Boolean(data.tablet)}
        mobile={Boolean(data.mobile)}
      >
        <div className={cx(styles.modelPartList)}>
          <Title overRuleH1="h3" firstText={title} underline variant="md" />

          <div className={cx(azCommonStyles['az-body-1-regular'])} ref={ref}>
            <Grid isULTag container spacing={1}>
              {filteredList.map(({ name, seoUrl }) => {
                if (seoUrl) {
                  return (
                    <Grid isListItem item sm={12} md={6} lg={4} key={seoUrl}>
                      <Link to={seoUrl} className={styles.modelItem}>
                        <span className={styles.modelLink}>{name}</span>
                      </Link>
                    </Grid>
                  );
                }

                return null;
              })}
            </Grid>
          </div>
        </div>
      </DeviceWrapper>
      <span className={styles.readMoreSmallProdDesc}>
        {shopByMMYListLength > maxOptions && !readMore && deviceType !== 'bot' && (
          <button
            className={cx(azCommonStyles['az-button-text'], styles.showMoreButton)}
            data-testid="description-read-more"
            onClick={toggleReadMore}
          >
            {labels.label_Show_All}
          </button>
        )}

        {readMore && deviceType !== 'bot' && (
          <button
            className={cx(azCommonStyles['az-button-text'], styles.showMoreButton)}
            data-testid="description-read-less"
            onClick={toggleReadMore}
          >
            {labels.label_Show_Less}
          </button>
        )}
      </span>
    </div>
  );
};
